// Was ein Bereich im Baum zusätzlich zeigt (docs/shell.md). Explorer.svelte
// bleibt ohne Domänenwissen -- es fragt hier nach.
//
//   kopf        ein Handgriff neben dem Namen des Bereichs
//   fuss        eine feste Fläche unten
//   abschnitte  statt EINES Baums mehrere Flächen untereinander, wie die
//               Seitenleiste in VS Code: je ein Kopf mit Lage-Satz, der
//               zuklappt (docs/gestaltung.md: eine Fläche je Gruppe, die
//               Lage im Kopf). Angedockte stehen unten, lassen sich
//               tauschen und in der Höhe ziehen; die übrigen füllen oben.
//
// Bisher nur Claude (docs/claude.md): oben die Seiten Übersicht und Gedächtnis,
// darunter Chats · Änderungen · Commits · Gedächtnis · Verbrauch.

import type { Component } from "svelte";

import BaumAenderungen from "../claude/BaumAenderungen.svelte";
import BaumCommits from "../claude/BaumCommits.svelte";
import BaumGedaechtnis from "../claude/BaumGedaechtnis.svelte";
import BaumKopf from "../claude/BaumKopf.svelte";
import BaumVerbrauch from "../claude/BaumVerbrauch.svelte";
import BaumZu from "../claude/BaumZu.svelte";
import { aenderungenLage, chatsLage, claudeSeiten, commitsLage, gedaechtnisLage, verbrauchLage } from "../claude/baum.svelte";
import { t } from "../i18n.svelte";
import type { LageTeil } from "../lage";
import type { SectionId } from "../nav-sections";
import type { BaumKnoten } from "./baum-knoten.svelte";

export interface ExplorerAbschnitt {
  id: string;
  titel: () => string;
  /** Lage-Satz im Kopf; mit lageNurZu nur zugeklappt (offen sagt es der Inhalt). */
  lage?: () => LageTeil[];
  lageNurZu?: boolean;
  /** Eigene Fläche; ohne steht hier der Baum des Bereichs (tree.svelte.ts).
   *  `fest`: die Höhe ist von Hand gezogen -- die Fläche darf sich dann in
   *  einer ersten Stufe kompakter zeigen (Verbrauch), bevor „⋯ weitere"
   *  greift. */
  // any: Flächen ohne Props (Änderungen, Commits) sollen nicht so tun
  // müssen, als nähmen sie `fest`.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inhalt?: Component<any>;
  /** Handgriff rechts im Kopf, z. B. „+". */
  kopf?: Component;
  /** Zugeklappt steht statt des Lage-Satzes das hier im Kopf: Symbole und
   *  Füllbalken (Tim 19.10.2026: „besser als Text"). Der Satz bleibt als
   *  aria-label. `zuProps` geht an die Komponente. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  zu?: Component<any>;
  zuProps?: Record<string, unknown>;
  standardOffen?: boolean;
  /** Unten angedockt: Reihenfolge und Höhe zieht man selbst. Ohne füllt
   *  der Abschnitt oben den Rest. */
  andocken?: boolean;
}

export interface ExplorerZusatz {
  kopf?: Component;
  fuss?: Component;
  /** Feste Seiten oben, vor den Abschnitten -- Baumzeilen ohne Abschnitt
   *  (Claude: Übersicht und Gedächtnis; Tim 21.10.2026: „über den Chats
   *  noch die Übersicht und das Gedächtnis als Seite eintragen. Dann kommt
   *  man schnell hin"). */
  seiten?: () => BaumKnoten[];
  abschnitte?: ExplorerAbschnitt[];
}

const ZUSATZ: Partial<Record<SectionId, ExplorerZusatz>> = {
  claude: {
    seiten: claudeSeiten,
    abschnitte: [
      { id: "chats", titel: () => t("claude.abschnitt.chats", "Chats"), lage: chatsLage, kopf: BaumKopf, standardOffen: true, zu: BaumZu, zuProps: { art: "chats" } },
      { id: "aenderungen", titel: () => t("claude.abschnitt.aenderungen", "Änderungen"), lage: aenderungenLage, inhalt: BaumAenderungen, standardOffen: true, andocken: true, zu: BaumZu, zuProps: { art: "aenderungen" } },
      { id: "commits", titel: () => t("claude.abschnitt.commits", "Commits"), lage: commitsLage, inhalt: BaumCommits, standardOffen: true, andocken: true, zu: BaumZu, zuProps: { art: "commits" } },
      // Die Zugriffe auf das Gedächtnis (docs/gedaechtnis.md): offen die
      // jüngsten, zugeklappt Lupe und Stift mit der Zahl der letzten Stunde.
      { id: "gedaechtnis", titel: () => t("gedaechtnis.title", "Gedächtnis"), lage: gedaechtnisLage, inhalt: BaumGedaechtnis, standardOffen: true, andocken: true, zu: BaumZu, zuProps: { art: "gedaechtnis" } },
      { id: "verbrauch", titel: () => t("claude.verbrauch", "Verbrauch"), lage: verbrauchLage, lageNurZu: true, inhalt: BaumVerbrauch, andocken: true, zu: BaumZu, zuProps: { art: "verbrauch" } },
    ],
  },
};

export function zusatzFuer(sektion: SectionId): ExplorerZusatz {
  return ZUSATZ[sektion] ?? {};
}
